import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import {
  BodyText,
  DisplayText,
  FadeView,
  Icon,
  LoadingDots,
  theme,
  useBreakpointHelpers,
  VStack,
} from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useCustomer } from '@order/Customer';
import { TierName } from '@order/graphql';
import { useSweetpass } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTrackEffect } from '@order/Telemetry';
import { getAppVersion } from '@order/utils';

import {
  useContactUsHandler,
  useKustomerAutoStart,
  useKustomerAvailability,
} from '../../../integrations/kustomer';
import type { AccountTabScreenId } from '../../../navigation/AppNavigation.props';
import {
  accountMenuStaticLinkConfig,
  resetStacksPostLogout,
} from '../../../screens/AccountScreen/AccountScreen.navigation';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';
import { AccountMenuIconItem } from './AccountMenuIconItem';
import { AccountMenuLink } from './AccountMenuLink';
import { useAccountLinks, useSocialLinks } from './AccountMenuLink.links';
import { AccountMenuPrimaryItem } from './AccountMenuPrimaryItem';
import { AccountMenuSecondaryItem } from './AccountMenuSecondaryItem';
import { useCustomerNameQuery } from './GraphQL/AccountMenu.generated';

export const AccountMenu = ({
  variant,
  screen: screenId,
}: AccountMenuProps): React.ReactElement => {
  const [response] = useCustomerNameQuery();
  const { isSigningOut, signOut } = useCustomer();
  const isLoggedIn = useIsLoggedIn();
  const { isSweetpassEnabled, loyaltyProfile } = useSweetpass();

  const sweetpassTier = isSweetpassEnabled
    ? loyaltyProfile?.tier?.name
    : undefined;

  useKustomerAutoStart();
  useTrackEffect('account.view');

  const signOutAndReset = React.useCallback(async () => {
    await signOut();
    resetStacksPostLogout();
  }, [signOut]);

  return (
    <AccountMenuContent
      variant={variant}
      screenId={screenId}
      isLoggedIn={isLoggedIn}
      isSigningOut={isSigningOut}
      customerName={response.data?.currentCustomer?.firstName}
      sweetpassTier={sweetpassTier}
      onSignOut={signOutAndReset}
    />
  );
};

const AccountMenuContent = ({
  variant,
  screenId,
  isLoggedIn,
  isSigningOut,
  customerName,
  sweetpassTier,
  onSignOut,
}: AccountMenuContentProps): React.ReactElement => {
  const { t } = useLocalizationContext();
  const { isMobile } = useBreakpointHelpers();
  const accountLinks = useAccountLinks();
  const socialLinks = useSocialLinks(true);
  const isReferFriendEnabled = useFeatureFlag('permanent-refer-friend-enabled');

  const isLoggedOutMessageVisible = !isLoggedIn;
  const isCustomerNameVisible = isLoggedIn && variant === 'STANDALONE';
  const isPrimaryMenuVisible = isLoggedIn;
  const isSecondaryMenuVisible =
    Platform.OS !== 'web' && variant === 'STANDALONE';
  const isIconRowVisible = isSecondaryMenuVisible;
  const isSignOutItemVisible = isLoggedIn;

  const isKustomerAvailable = useKustomerAvailability();
  const { handleContactUs, handlingContactUs } = useContactUsHandler(
    isKustomerAvailable,
    'account',
  );

  const isSweetpassJoinItemVisible =
    Boolean(sweetpassTier) && sweetpassTier === TierName.OptedOut;
  const isSweetpassMembershipItemVisible =
    Boolean(sweetpassTier) && sweetpassTier !== TierName.OptedOut;

  return (
    <VStack
      testID={
        variant === 'STANDALONE'
          ? 'account-menu-standalone'
          : 'account-menu-sidebar'
      }
      style={variant === 'STANDALONE' && styles.standaloneContainer}
      customSpacing={0}
    >
      {isLoggedOutMessageVisible ? <AccountSignedOutMessage /> : null}
      {isCustomerNameVisible ? (
        <DisplayText
          testID="account.menu-customer-name"
          style={styles.title}
          size={3}
        >
          {customerName}
        </DisplayText>
      ) : null}
      {isPrimaryMenuVisible ? (
        <>
          <AccountMenuLink
            testID="account.sidebar-tab.profile"
            label={t('account.sidebar.profile')}
            iconName="IconProfileStroke"
            to={accountMenuStaticLinkConfig.profileLinkConfig}
            active={screenId === 'profile'}
          />
          {isSweetpassJoinItemVisible ? (
            <>
              <PrimaryItemDivider variant={variant} />
              <AccountMenuLink
                testID="account.sidebar-tab.sweetpass-join"
                label={t('account.sidebar.sweetpass-join')}
                iconName="IconLoyalty"
                tag={{
                  title: t('account.sidebar.sweetpass-join.tag'),
                  palette: 'primary',
                }}
                to={accountMenuStaticLinkConfig.sweetpassJoinLinkConfig}
                active={false}
              />
            </>
          ) : null}
          {isSweetpassMembershipItemVisible ? (
            <>
              <PrimaryItemDivider variant={variant} />
              <AccountMenuLink
                testID="account.sidebar-tab.sweetpass-membership"
                label={t('account.sidebar.sweetpass-membership')}
                iconName="IconLoyalty"
                to={accountMenuStaticLinkConfig.sweetpassMembershipLinkConfig}
                active={screenId === 'sweetpass-membership'}
              />
            </>
          ) : null}
          <PrimaryItemDivider variant={variant} />
          <AccountMenuLink
            testID="account.sidebar-tab.payment-credit"
            label={t('account.sidebar.payment-credit')}
            iconName="IconCreditCard"
            to={accountMenuStaticLinkConfig.paymentCreditLinkConfig}
            active={screenId === 'payment-credit'}
          />
          <PrimaryItemDivider variant={variant} />
          <AccountMenuLink
            testID="account.sidebar-tab.addresses"
            label={t('account.sidebar.addresses')}
            iconName="IconPickupStrokeSm"
            to={accountMenuStaticLinkConfig.addressesLinkConfig}
            active={screenId === 'addresses'}
          />
          <PrimaryItemDivider variant={variant} />
          <AccountMenuLink
            testID="account.sidebar-tab.orders"
            label={t('account.sidebar.orders')}
            iconName="IconBowl"
            to={accountMenuStaticLinkConfig.ordersLinkConfig}
            active={screenId === 'orders'}
          />
          <PrimaryItemDivider variant={variant} />
          <AccountMenuLink
            testID="account.sidebar-tab.favorites"
            label={t('account.sidebar.favorites')}
            iconName="IconHeartStroke"
            to={accountMenuStaticLinkConfig.favoritesLinkConfig}
            active={screenId === 'favorites'}
          />
          <PrimaryItemDivider variant={variant} />
          {isReferFriendEnabled ? (
            <>
              <AccountMenuLink
                testID="account.sidebar-tab.refer-friend"
                label={t('account.sidebar.refer-friend')}
                iconName="IconStarStroke"
                to={accountMenuStaticLinkConfig.referFriendLinkConfig}
                active={screenId === 'refer-friend'}
              />
              <PrimaryItemDivider variant={variant} />
            </>
          ) : null}
          <AccountMenuLink
            testID="account.sidebar-tab.dietary-restrictions"
            label={t('account.sidebar.dietary-restrictions')}
            iconName="IconFilter"
            to={accountMenuStaticLinkConfig.dietaryRestrictionsLinkConfig}
            active={screenId === 'dietary-restrictions'}
          />
          {(isMobile || !isKustomerAvailable) && !isSecondaryMenuVisible ? (
            <>
              <PrimaryItemDivider variant={variant} />
              <View style={styles.row}>
                <AccountMenuPrimaryItem
                  testID="account.sidebar-tab.contact-us"
                  label={t('account.sidebar.contact-us')}
                  iconName="IconText"
                  onPress={handleContactUs}
                />
                {!isKustomerAvailable ? (
                  <View style={styles.kustomerIcon}>
                    <Icon name="IconShare2" />
                  </View>
                ) : null}
                <FadeView show={handlingContactUs} style={styles.kustomerIcon}>
                  <LoadingDots />
                </FadeView>
              </View>
            </>
          ) : null}
        </>
      ) : null}
      {isSecondaryMenuVisible ? (
        <>
          <SectionDivider variant={variant} />
          <View style={styles.row}>
            <AccountMenuSecondaryItem
              testID="account.menu-secondary-item.contact-us"
              label={t('account.sidebar.contact-us')}
              onPress={handleContactUs}
            />
            {!isKustomerAvailable ? (
              <View style={styles.kustomerIcon}>
                <Icon name="IconShare2" />
              </View>
            ) : null}
            <FadeView show={handlingContactUs} style={styles.kustomerIcon}>
              <LoadingDots />
            </FadeView>
          </View>
          <AccountMenuSecondaryItem
            testID="account-menu.secondary-item.terms"
            label={t('account.sidebar.terms')}
            onPress={accountLinks.handleTermsLink}
          />
          <AccountMenuSecondaryItem
            testID="account-menu.secondary-item.privacy-policy"
            label={t('account.sidebar.privacy-policy')}
            onPress={accountLinks.handlePrivacyLink}
          />
          <AccountMenuSecondaryItem
            testID="account-menu.secondary-item.do-not-sell"
            label={t('account.sidebar.do-not-sell')}
            onPress={accountLinks.handlePersonalInfoLink}
          />
        </>
      ) : null}
      {isIconRowVisible ? (
        <View style={styles.iconItemsContainer}>
          <AccountMenuIconItem
            testID="account.menu.icon-item.instagram"
            label={t('account.sidebar.instagram')}
            iconName="IconInstagram"
            onPress={socialLinks.handleInstagramTelemetry}
          />
          <AccountMenuIconItem
            testID="account.menu.icon-item.facebook"
            label={t('account.sidebar.facebook')}
            iconName="IconFacebook"
            onPress={socialLinks.handleFacebookTelemetry}
          />
          <AccountMenuIconItem
            testID="account.menu.icon-item.tiktok"
            label={t('account.sidebar.tiktok')}
            iconName="IconTikTok"
            onPress={socialLinks.handleTiktokTelemetry}
          />
          <AccountMenuIconItem
            testID="account.menu.icon-item.twitter"
            label={t('account.sidebar.twitter')}
            iconName="IconTwitter"
            onPress={socialLinks.handleTwitterTelemetry}
          />
          <AccountMenuIconItem
            testID="account.menu.icon-item.spotify"
            label={t('account.sidebar.spotify')}
            iconName="IconSpotify"
            onPress={socialLinks.handleSpotifyTelemetry}
          />
        </View>
      ) : null}
      {isSignOutItemVisible ? (
        <>
          <SectionDivider variant={variant} />
          <AccountMenuPrimaryItem
            testID="account.sidebar-tab.sign-out"
            label={t('account.sidebar.sign-out')}
            iconName="IconLogout"
            disabled={isSigningOut}
            onPress={onSignOut}
          />
        </>
      ) : null}

      <BodyText style={styles.appVersionText}>v{getAppVersion()}</BodyText>
    </VStack>
  );
};

const SectionDivider = ({ variant }: DividerProps) => {
  const style = variant === 'STANDALONE' ? styles.hardDivider : styles.divider;

  return <View style={style} />;
};

const PrimaryItemDivider = ({ variant }: DividerProps) => {
  if (variant !== 'STANDALONE') return null;

  return <View style={styles.softDivider} />;
};

type AccountMenuVariant = 'STANDALONE' | 'SIDEBAR';

type AccountMenuProps = {
  readonly variant: AccountMenuVariant;
  readonly screen?: AccountTabScreenId | null;
};

type DividerProps = { readonly variant: AccountMenuVariant };

type AccountMenuContentProps = {
  readonly variant: AccountMenuVariant;
  readonly screenId?: AccountTabScreenId | null;
  readonly isLoggedIn: boolean;
  readonly isSigningOut: boolean;
  readonly customerName?: string | null;
  readonly sweetpassTier?: TierName;
  readonly onSignOut: () => void;
};

const styles = StyleSheet.create({
  title: {
    fontSize: 32,
    paddingVertical: theme.spacing.large,
  },
  iconItemsContainer: {
    marginTop: theme.spacing.medium,
    flexDirection: 'row',
    alignItems: 'center',
  },
  standaloneContainer: {
    marginRight: theme.spacing.xlarge - theme.spacing.small,
    width: '100%',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  kustomerIcon: {
    marginLeft: theme.spacing.small,
  },
  softDivider: {
    width: '100%',
    height: 1,
    marginVertical: theme.spacing.small,
    backgroundColor: theme.colors.LIGHT,
  },
  divider: {
    width: '100%',
    height: 1,
    marginVertical: theme.spacing.medium,
    backgroundColor: theme.colors.LIGHT,
  },
  hardDivider: {
    width: '100%',
    height: 1,
    marginVertical: theme.spacing.large,
    backgroundColor: theme.colors.DARK_KALE,
  },
  appVersionText: {
    marginTop: theme.spacing.large,
  },
});
